import { useEffect, useState } from "react";
import { ArrowDownCircle, ArrowUpCircle, Building2, CreditCard, Landmark, Scale, TrendingUp, Wallet } from "lucide-react";
import api from "../api";
import DataTable from "../components/DataTable";
import StatCard from "../components/StatCard";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { fmtDate, fmtMoney } from "../utils/format";

export default function Accounting() {
  const [data, setData] = useState(null);
  const [pnl, setPnl] = useState(null);
  const [dates, setDates] = useState({ date_from: "", date_to: "" });

  const load = () => {
    const p = new URLSearchParams();
    if (dates.date_from) p.set("date_from", dates.date_from);
    if (dates.date_to) p.set("date_to", dates.date_to);
    api.get(`/accounting/summary?${p}`).then((r) => setData(r.data)).catch(() => {});
    api.get(`/accounting/pnl?${p}`).then((r) => setPnl(r.data)).catch(() => setPnl(null));
  };
  useEffect(() => { load(); }, [dates]);

  if (!data) return <div className="flex h-96 items-center justify-center"><div className="h-10 w-10 animate-spin rounded-full border-4 border-[#2563EB] border-t-transparent" /></div>;

  return (
    <div className="space-y-6" data-testid="accounting-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900">Comptabilité</h1>
          <p className="mt-1 text-sm text-slate-500">Recettes, dépenses, soldes et résultat — alimentés automatiquement par les opérations.</p>
        </div>
        <div className="flex items-end gap-2">
          <div><Label className="text-xs">Du</Label><Input type="date" value={dates.date_from} onChange={(e) => setDates({ ...dates, date_from: e.target.value })} data-testid="accounting-from" /></div>
          <div><Label className="text-xs">Au</Label><Input type="date" value={dates.date_to} onChange={(e) => setDates({ ...dates, date_to: e.target.value })} data-testid="accounting-to" /></div>
        </div>
      </div>

      {pnl && (
        <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="pnl-section">
          <h3 className="font-heading mb-4 flex items-center gap-2 text-base font-semibold text-slate-800">
            <TrendingUp className="h-4 w-4 text-cyan-600" /> Compte de résultat {dates.date_from ? `(${dates.date_from} → ${dates.date_to})` : "(toutes périodes)"}
          </h3>
          <div className="grid grid-cols-2 gap-x-8 gap-y-2 text-sm md:grid-cols-2">
            <div className="flex justify-between border-b border-slate-100 py-2"><span className="text-slate-600">Ventes de véhicules</span><span className="font-mono-num font-semibold">{fmtMoney(pnl.ca_ventes)}</span></div>
            <div className="flex justify-between border-b border-slate-100 py-2"><span className="text-slate-600">Locations</span><span className="font-mono-num font-semibold">{fmtMoney(pnl.ca_locations)}</span></div>
            <div className="flex justify-between border-b border-slate-100 py-2 font-semibold"><span>Chiffre d'affaires</span><span className="font-mono-num text-cyan-700">{fmtMoney(pnl.chiffre_affaires)}</span></div>
            <div className="flex justify-between border-b border-slate-100 py-2"><span className="text-slate-600">Coût des véhicules vendus</span><span className="font-mono-num text-red-600">−{fmtMoney(pnl.cout_vehicules_vendus)}</span></div>
            <div className="flex justify-between border-b border-slate-100 py-2 font-semibold"><span>Bénéfice brut</span><span className={`font-mono-num ${pnl.benefice_brut >= 0 ? "text-emerald-600" : "text-red-600"}`} data-testid="pnl-gross-profit">{fmtMoney(pnl.benefice_brut)}</span></div>
            <div className="flex justify-between border-b border-slate-100 py-2"><span className="text-slate-600">Dépenses véhicules</span><span className="font-mono-num text-red-600">−{fmtMoney(pnl.depenses_vehicules)}</span></div>
            <div className="flex justify-between border-b border-slate-100 py-2"><span className="text-slate-600">Dépenses générales</span><span className="font-mono-num text-red-600">−{fmtMoney(pnl.depenses_generales)}</span></div>
            <div className="flex justify-between py-2 text-base font-bold"><span>Bénéfice net (encaissé)</span><span className={`font-mono-num ${pnl.benefice_net >= 0 ? "text-emerald-600" : "text-red-600"}`} data-testid="pnl-net-profit">{fmtMoney(pnl.benefice_net)}</span></div>
          </div>
        </div>
      )}

            <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard icon={ArrowUpCircle} label="Recettes" value={fmtMoney(data.total_recettes)} tone="emerald" testid="acc-recettes" />
        <StatCard icon={ArrowDownCircle} label="Dépenses" value={fmtMoney(data.total_depenses)} tone="red" testid="acc-depenses" />
        <StatCard icon={Scale} label="Résultat (bénéfice/perte)" value={fmtMoney(data.resultat)} tone={data.resultat >= 0 ? "emerald" : "red"} testid="acc-resultat" />
        <StatCard icon={CreditCard} label="Créances clients" value={fmtMoney(data.creances_clients)} tone="amber" testid="acc-creances" />
        <StatCard icon={Wallet} label="Solde caisse" value={fmtMoney(data.solde_caisse)} tone="blue" testid="acc-caisse" />
        <StatCard icon={Landmark} label="Solde banques" value={fmtMoney(data.solde_banque)} tone="sky" testid="acc-banque" />
        <StatCard icon={Building2} label="Dettes fournisseurs" value={fmtMoney(data.dettes_fournisseurs)} tone="red" testid="acc-dettes" />
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        <div>
          <h3 className="font-heading mb-3 text-base font-semibold text-emerald-700">Journal des recettes</h3>
          <div data-testid="journal-recettes-table">
          <DataTable testidPrefix="journal-recettes" rows={data.journal_recettes} pageSize={8}
            searchKeys={["description", "reference"]}
            columns={[
              { key: "date", label: "Date", render: (r) => fmtDate(r.date) },
              { key: "description", label: "Description", render: (r) => <span className="text-slate-600">{r.description}</span> },
              { key: "account_name", label: "Compte" },
              { key: "amount", label: "Montant", className: "text-right", render: (r) => <span className="font-mono-num font-semibold text-emerald-600">+{fmtMoney(r.amount)}</span> },
            ]} />
          </div>
        </div>
        <div>
          <h3 className="font-heading mb-3 text-base font-semibold text-red-700">Journal des dépenses</h3>
          <div data-testid="journal-depenses-table">
          <DataTable testidPrefix="journal-depenses" rows={data.journal_depenses} pageSize={8}
            searchKeys={["description", "reference"]}
            columns={[
              { key: "date", label: "Date", render: (r) => fmtDate(r.date) },
              { key: "description", label: "Description", render: (r) => <span className="text-slate-600">{r.description}</span> },
              { key: "account_name", label: "Compte" },
              { key: "amount", label: "Montant", className: "text-right", render: (r) => <span className="font-mono-num font-semibold text-red-600">−{fmtMoney(r.amount)}</span> },
            ]} />
          </div>
        </div>
      </div>
    </div>
  );
}
